/**
 * Search Modal Component
 *
 * Used on mobile only (< 768px) where the inline header search is hidden.
 * Shares search logic with HeaderBar via useProductSearch hook.
 */

import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Modal,
  TextField,
  Box,
  Text,
  BlockStack,
  InlineStack,
  Button,
  Spinner,
  Thumbnail,
} from '@shopify/polaris';
import { SearchIcon, ClockIcon, DeleteIcon } from '@shopify/polaris-icons';
import { useProductSearch } from '../../hooks/useProductSearch';

interface SearchModalProps {
  open: boolean;
  onClose: () => void;
}

export function SearchModal({ open, onClose }: SearchModalProps) {
  const navigate = useNavigate();

  const {
    query,
    results,
    loading,
    recentSearches,
    handleQueryChange,
    saveRecentSearch,
    clearRecentSearches,
    loadRecentSearches,
    reset,
    searchProducts,
    setQuery,
  } = useProductSearch();

  // Load recent searches when modal opens
  useEffect(() => {
    if (open) loadRecentSearches();
  }, [open, loadRecentSearches]);

  // Reset on close
  useEffect(() => {
    if (!open) reset();
  }, [open, reset]);

  const handleProductClick = (slug: string) => {
    saveRecentSearch(query);
    onClose();
    navigate(`/products/${slug}`);
  };

  const handleSearch = () => {
    if (query.trim()) {
      saveRecentSearch(query);
      onClose();
      navigate(`/search?q=${encodeURIComponent(query)}`);
    }
  };

  const handleRecentSearchClick = (term: string) => {
    setQuery(term);
    searchProducts(term);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') handleSearch();
  };

  return (
    <Modal open={open} onClose={onClose} title="Search Products">
      <Modal.Section>
        <BlockStack gap="400">
          <div onKeyDown={handleKeyDown}>
            <TextField
              label="Search"
              labelHidden
              value={query}
              onChange={handleQueryChange}
              placeholder="Search for products..."
              prefix={<SearchIcon />}
              autoComplete="off"
              autoFocus
              clearButton
              onClearButtonClick={() => handleQueryChange('')}
            />
          </div>

          {loading && (
            <Box padding="400">
              <InlineStack align="center">
                <Spinner size="small" />
                <Text as="span" tone="subdued">Searching...</Text>
              </InlineStack>
            </Box>
          )}

          {!loading && query && results.length > 0 && (
            <BlockStack gap="200">
              <Text as="h3" variant="headingSm">
                Products ({results.length})
              </Text>
              {results.map((product) => (
                <div
                  key={product.id}
                  style={{
                    padding: '12px',
                    backgroundColor: 'var(--p-color-bg-surface-secondary)',
                    borderRadius: '8px',
                    cursor: 'pointer',
                  }}
                  onClick={() => handleProductClick(product.slug)}
                >
                  <InlineStack gap="300" blockAlign="center">
                    <Thumbnail
                      source={product.primaryImage || 'https://placehold.co/40x40/e2e8f0/64748b?text=No+Img'}
                      alt={product.name}
                      size="small"
                    />
                    <BlockStack gap="050">
                      <Text as="p" variant="bodyMd" fontWeight="semibold">
                        {product.name}
                      </Text>
                      <InlineStack gap="200">
                        <Text as="span" variant="bodySm" tone="subdued">
                          {product.categoryName}
                        </Text>
                        {product.startingPrice && (
                          <Text as="span" variant="bodySm">
                            From ₹{product.startingPrice.toLocaleString()}
                          </Text>
                        )}
                      </InlineStack>
                    </BlockStack>
                  </InlineStack>
                </div>
              ))}
              <Button fullWidth onClick={handleSearch}>
                View all results for "{query}"
              </Button>
            </BlockStack>
          )}

          {!loading && query && results.length === 0 && (
            <Box padding="400">
              <BlockStack align="center" gap="200">
                <Text as="p" tone="subdued">
                  No products found for "{query}"
                </Text>
                <Button onClick={handleSearch}>
                  Search anyway
                </Button>
              </BlockStack>
            </Box>
          )}

          {!query && recentSearches.length > 0 && (
            <BlockStack gap="200">
              <InlineStack align="space-between" blockAlign="center">
                <Text as="h3" variant="headingSm">
                  Recent Searches
                </Text>
                <Button
                  variant="plain"
                  icon={DeleteIcon}
                  onClick={clearRecentSearches}
                >
                  Clear all
                </Button>
              </InlineStack>
              {recentSearches.map((search) => (
                <div
                  key={search}
                  style={{ padding: '8px', cursor: 'pointer' }}
                  onClick={() => handleRecentSearchClick(search)}
                >
                  <InlineStack gap="200" blockAlign="center">
                    <ClockIcon />
                    <Text as="span" variant="bodyMd">
                      {search}
                    </Text>
                  </InlineStack>
                </div>
              ))}
            </BlockStack>
          )}

          {!query && recentSearches.length === 0 && (
            <Box padding="400">
              <Text as="p" tone="subdued" alignment="center">
                Start typing to search for products
              </Text>
            </Box>
          )}
        </BlockStack>
      </Modal.Section>
    </Modal>
  );
}
